/**
 * Calcul des variables dynamiques pour la résolution des placeholders {{xxx}}
 * dans les blocs librairie et les sections Tiptap au moment de la génération PDF.
 *
 * Variables supportées :
 *   {{site}}        : nom du site
 *   {{adresse}}     : adresse du site
 *   {{date}}        : date de la campagne (format fr-FR long)
 *   {{nb_mesures}}  : nombre de mesures avec épaisseur relevée
 *   {{min}}         : pourcentage de corrosion minimum (1 décimale)
 *   {{max}}         : pourcentage de corrosion maximum (1 décimale)
 *   {{moyenne}}     : pourcentage de corrosion moyen (1 décimale)
 */

import type { SiteExportData } from "@/modules/corrosion/export/data-fetcher";

export type RapportVariables = Record<string, string>;

export function computeRapportVariables(data: SiteExportData): RapportVariables {
  const { site, campagne, zones } = data;

  // Collecter toutes les mesures avec pourcentageCorrosion non nul
  const corrosionValues: number[] = [];
  let totalMesures = 0;

  for (const zoneData of zones) {
    for (const pinData of zoneData.pins) {
      if (pinData.mesure) {
        totalMesures++;
        const pct = pinData.mesure.pourcentageCorrosion;
        if (pct != null) {
          corrosionValues.push(pct);
        }
      }
    }
  }

  const fmt1 = (n: number) => n.toFixed(1);
  const NA = "N/A";

  const min = corrosionValues.length > 0 ? fmt1(Math.min(...corrosionValues)) : NA;
  const max = corrosionValues.length > 0 ? fmt1(Math.max(...corrosionValues)) : NA;
  const moyenne =
    corrosionValues.length > 0
      ? fmt1(corrosionValues.reduce((a, b) => a + b, 0) / corrosionValues.length)
      : NA;

  const dateFr = new Date(campagne.date).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return {
    site: site.name ?? "",
    adresse: site.address ?? "",
    date: dateFr,
    nb_mesures: String(totalMesures),
    min,
    max,
    moyenne,
  };
}

/**
 * Remplace les occurrences de {{variable}} dans une chaîne de texte.
 * Les variables inconnues sont laissées intactes.
 */
export function resolveVariables(text: string, variables: RapportVariables): string {
  return text.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
    return Object.prototype.hasOwnProperty.call(variables, key) ? variables[key] : match;
  });
}
